/**
 * Tree generation and traversal utilities
 */

import type { TreeNode, Path, TreeElements } from "./types";

/**
 * Generates a binary tree structure for the tournament bracket
 */
export function generateTree(
  width: number,
  height: number,
  depth: number
): TreeNode {
  // Use consistent level height for all nodes including victory
  const levelHeight = height / (depth + 2);

  const createNode = (
    level: number,
    index: number,
    totalAtLevel: number,
    parent?: TreeNode
  ): TreeNode => {
    const spacing = width / (totalAtLevel + 1);
    const x = spacing * (index + 1);
    const y = levelHeight * (level + 2); // +2 to leave room for victory node at top

    const node: TreeNode = {
      id: `node-${level}-${index}`,
      x,
      y,
      level,
      parent,
    };

    // Create children for non-leaf nodes
    if (level < depth - 1) {
      const leftChild = createNode(
        level + 1,
        index * 2,
        totalAtLevel * 2,
        node
      );
      const rightChild = createNode(
        level + 1,
        index * 2 + 1,
        totalAtLevel * 2,
        node
      );
      node.children = [leftChild, rightChild];
      // Center parent between children
      node.x = (leftChild.x + rightChild.x) / 2;
    }

    return node;
  };

  const root = createNode(0, 0, 1);

  // Add victory node above root at the same vertical increment
  const victoryNode: TreeNode = {
    id: "node-victory",
    x: root.x,
    y: levelHeight * 1.5, // Same spacing as other levels
    level: -1, // Special level for victory node
    parent: undefined,
    children: [root],
  };

  // Link root to victory node
  root.parent = victoryNode;

  return victoryNode;
}

/**
 * Collects all nodes and paths from the tree structure
 */
export function collectAllElements(tree: TreeNode): TreeElements {
  const nodes: TreeNode[] = [];
  const paths: Path[] = [];

  const traverse = (node: TreeNode): void => {
    nodes.push(node);

    if (node.children) {
      node.children.forEach((child) => {
        // const midY = (node.y + child.y) / 2;
        const midY = node.y;
        paths.push({
          id: `path-${node.id}-${child.id}`,
          parent: node,
          child,
          midY,
          level: child.level,
        });
        traverse(child);
      });
    }
  };

  traverse(tree);
  return { nodes, paths };
}

/**
 * Gets all nodes at a specific level in the tree
 */
export function getNodesAtLevel(
  tree: TreeNode,
  targetLevel: number
): TreeNode[] {
  const result: TreeNode[] = [];

  const traverse = (node: TreeNode): void => {
    if (node.level === targetLevel) {
      result.push(node);
    }
    node.children?.forEach(traverse);
  };

  traverse(tree);
  return result;
}

/**
 * Validates tree structure integrity
 */
export function validateTree(tree: TreeNode): boolean {
  try {
    const visited = new Set<string>();

    const validate = (node: TreeNode): boolean => {
      if (visited.has(node.id)) {
        console.error(`Circular reference detected at node ${node.id}`);
        return false;
      }
      visited.add(node.id);

      if (node.children) {
        if (node.children.length !== 2) {
          console.error(
            `Node ${node.id} has ${node.children.length} children, expected 2`
          );
          return false;
        }

        for (const child of node.children) {
          if (child.parent !== node) {
            console.error(`Parent-child mismatch at ${node.id} -> ${child.id}`);
            return false;
          }
          if (!validate(child)) {
            return false;
          }
        }
      }

      return true;
    };

    return validate(tree);
  } catch (error) {
    console.error("Tree validation failed:", error);
    return false;
  }
}
